import music from './Music';

// Home-screen sound effects: hovers, clicks and a typewriter on the name field.
// Recordings are picked up from src/assets/ui-sounds by name, so any that
// haven't been added yet are simply silent:
//   hover.mp3            pointer over a dog card, Start or the sound toggle
//   click.mp3            pressing any of those
//   pick.mp3             picking a dog (falls back to click.mp3)
//   key-1.mp3, key-2...  one per typed character, chosen at random
//   backspace.mp3        deleting (falls back to a key sound)
// They're kept out of src/assets/sounds, which is the in-game audio sprite.
const files = require.context('../assets/ui-sounds', false, /\.(mp3|ogg)$/);

const VOLUME = {hover: 0.35, click: 0.6, pick: 0.6, key: 0.5, backspace: 0.5};
// keystrokes are nudged up or down in pitch so a word doesn't sound like a loop
const KEY_DETUNE = 0.06;
const HOVER_GAP_MS = 70;

class MenuSfx {
  constructor() {
    this.context = null;
    this.buffers = {}; // name -> [AudioBuffer]
    this.lastHover = 0;
    this.resume = this.resume.bind(this);
  }

  /**
   * load
   * Decodes every recording up front, so the first hover isn't late. Safe to
   * call more than once.
   */
  load() {
    if (this.context || !(window.AudioContext || window.webkitAudioContext)) {
      return;
    }
    const Context = window.AudioContext || window.webkitAudioContext;
    this.context = new Context();
    // browsers start audio suspended until the first click or key press
    document.addEventListener('pointerdown', this.resume, true);
    document.addEventListener('keydown', this.resume, true);

    files.keys().forEach((key) => {
      const name = key.replace(/^\.\//, '').replace(/(-\d+)?\.\w+$/, '');
      fetch(files(key))
        .then((response) => response.arrayBuffer())
        .then((data) => new Promise((resolve, reject) => {
          this.context.decodeAudioData(data, resolve, reject);
        }))
        .then((buffer) => {
          (this.buffers[name] = this.buffers[name] || []).push(buffer);
        })
        .catch(() => {});
    });
  }

  resume() {
    if (this.context && this.context.state === 'suspended') {
      this.context.resume();
    }
  }

  /**
   * play
   * @param {String} name
   * @param {Number} [rate=1] playback rate, which also shifts the pitch
   * @param {String} [fallback] played instead when name has no recording
   */
  play(name, rate, fallback) {
    if (!this.context || music.muted || this.context.state !== 'running') {
      return;
    }
    let takes = this.buffers[name];
    if (!takes && fallback) {
      takes = this.buffers[fallback];
      name = fallback;
    }
    if (!takes) {
      return;
    }
    const source = this.context.createBufferSource();
    source.buffer = takes[Math.floor(Math.random() * takes.length)];
    source.playbackRate.value = rate || 1;
    const gain = this.context.createGain();
    gain.gain.value = VOLUME[name] || 0.5;
    source.connect(gain).connect(this.context.destination);
    source.start();
  }

  hover() {
    const now = Date.now();
    if (now - this.lastHover < HOVER_GAP_MS) {
      return;
    }
    this.lastHover = now;
    this.play('hover');
  }

  click() {
    this.play('click');
  }

  pick() {
    this.play('pick', 1, 'click');
  }

  /**
   * key
   * Driven by the input event rather than keydown: phone keyboards report
   * most keys as 'Unidentified', and this way a key that doesn't change the
   * name (an arrow, the 13th letter) stays quiet.
   * @param {InputEvent} event
   */
  key(event) {
    const rate = 1 + (Math.random() * 2 - 1) * KEY_DETUNE;
    const type = event.inputType || 'insertText';
    if (type.indexOf('delete') === 0) {
      this.play('backspace', rate, 'key');
    } else {
      this.play('key', rate);
    }
  }
}

export default new MenuSfx();
